import { Box, Dialog, Divider, IconButton, Stack, Tooltip, Typography, useTheme, Chip } from '@mui/material';
import { useForm, useFieldArray } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import StraightenIcon from '@mui/icons-material/Straighten';
import ScaleIcon from '@mui/icons-material/Scale';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CheckboxFormInput, NumberFieldFormInput, AutocompleteFormInput, TextFieldFormInput } from '../formInputs';
import StyledButton from '../buttons/StyledButton';
import { usePackageTypes } from '../../hooks/usePackageType';
import DateFieldFormInput from '../formInputs/dateInput/DateFieldFormInput';
import { yearMonthDateFormat } from '../../common/dateTimes';
import dayjs from 'dayjs';
import { useIsOnline } from '../../hooks';

interface PrintShippingLabelsFormProps {
  open: boolean;
  onSubmit: (data: PrintShippingLabelsModel) => void;
  onClose: () => void;
  data?: PrintShippingLabelsModel;
  isLargeItem: boolean;
  isDirectReceipt: boolean;
  isTransportBooking: boolean;
  asn: string;
}

export interface PackageData {
  id: number;
  total: number;
  packageType: string;
  hasDangerousGoods: boolean;
  height?: number;
  width?: number;
  depth?: number;
  weight?: number;
}

export interface PrintShippingLabelsModel {
  dateReceived?: string;
  docketNumber?: string;
  packages: PackageData[];
}

export default function PrintShippingLabelsForm({
  open,
  onSubmit,
  onClose,
  data,
  isLargeItem,
  isDirectReceipt,
  isTransportBooking,
  asn
}: PrintShippingLabelsFormProps) {
  const theme = useTheme();
  const packageTypes = usePackageTypes();
  const { isOnline } = useIsOnline();
  const defaultPackage: PackageData = {
    id: Date.now(),
    total: 1,
    packageType: 'Carton',
    hasDangerousGoods: false
  };
  const defaultValues: PrintShippingLabelsModel = data?.packages.length
    ? data
    : {
        docketNumber: asn,
        packages: [defaultPackage]
      };

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({ defaultValues });
  const { fields, append, remove } = useFieldArray({ control, name: 'packages' });
  function onSubmitForm(data: PrintShippingLabelsModel) {
    onSubmit(data);
  }

  function handleAddPackage() {
    append(defaultPackage);
  }

  const minDate = dayjs().subtract(1, 'year').format(yearMonthDateFormat);
  const maxDate = dayjs().format(yearMonthDateFormat);
  const buttonText = isDirectReceipt
    ? 'Complete Receipt'
    : isLargeItem || isTransportBooking
      ? 'Move to Staging'
      : 'Next: Assign to SSCC';
  return (
    <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Stack spacing={0}>
          {/* Header */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <LocalShippingIcon sx={{ fontSize: 24, color: 'text.primary' }} />
                <Typography variant="h6" fontWeight={600}>
                  Shipping Labels
                </Typography>
              </Stack>
              <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ padding: 3, maxHeight: '70vh', overflowY: 'auto', backgroundColor: 'grey.50' }}>
            <Stack spacing={3}>
              {/* Receipt Information Card */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  padding: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <ReceiptIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Receipt Information
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2
                  }}>
                  <Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                      <ReceiptIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Docket Number
                      </Typography>
                    </Stack>
                    <TextFieldFormInput
                      name="docketNumber"
                      required
                      label=""
                      control={control}
                      error={errors['docketNumber']}
                    />
                  </Box>
                  <Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Date Received
                      </Typography>
                    </Stack>
                    <DateFieldFormInput
                      name="dateReceived"
                      required
                      label=""
                      control={control}
                      error={errors?.dateReceived}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Package Cards */}
              <Stack spacing={2}>
                {fields.map((field, index) => (
                  <Box
                    key={field.id}
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: 1.5,
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}>
                    {/* Package Header */}
                    <Box
                      sx={{
                        padding: 1.5,
                        paddingX: 2,
                        backgroundColor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InventoryIcon sx={{ color: 'text.primary', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Package {index + 1}
                        </Typography>
                        {fields.length > 1 && (
                          <Chip
                            label={`of ${fields.length}`}
                            size="small"
                            sx={{
                              backgroundColor: 'grey.200',
                              height: 20,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Stack>
                      {fields.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => remove(index)}
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'error.lighter' }
                          }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {/* Package Content */}
                    <Box sx={{ padding: 2.5 }}>
                      <Stack spacing={2.5}>
                        {/* Basic Information Section */}
                        <Box>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                              gap: 2
                            }}>
                            <Box>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                  Quantity
                                </Typography>
                              </Stack>
                              <NumberFieldFormInput
                                name={`packages[${index}].total`}
                                label=""
                                required
                                min={1}
                                control={control}
                                error={errors?.packages?.[index]?.total}
                              />
                            </Box>
                            <Box>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                <CategoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                  Package Type
                                </Typography>
                              </Stack>
                              <AutocompleteFormInput
                                name={`packages[${index}].packageType`}
                                label=""
                                placeholder="Search or select..."
                                control={control}
                                required
                                options={
                                  packageTypes?.map((type) => ({
                                    name: type,
                                    value: type
                                  })) || [{ name: 'Carton', value: 'Carton' }]
                                }
                                error={errors?.packages?.[index]?.packageType}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Dangerous Goods Section */}
                        <Box
                          sx={{
                            padding: 2,
                            backgroundColor: 'warning.lighter',
                            borderRadius: 2,
                            border: `2px dashed ${theme.palette.warning.main}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                          }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: 'warning.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                            <WarningAmberIcon sx={{ color: 'white', fontSize: 24 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="warning.dark">
                              Dangerous Goods
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Check if this package contains hazardous materials
                            </Typography>
                          </Box>
                          <CheckboxFormInput
                            name={`packages[${index}].hasDangerousGoods`}
                            control={control}
                            error={errors?.packages?.[index]?.hasDangerousGoods}
                          />
                        </Box>

                        {/* Dimensions Section (for large items) */}
                        {(isLargeItem || isTransportBooking) && (
                          <Box
                            sx={{
                              padding: 2,
                              backgroundColor: 'grey.50',
                              borderRadius: 2,
                              border: `1px solid ${theme.palette.divider}`
                            }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                              <StraightenIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                Dimensions & Weight
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 2
                              }}>
                              <Box>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                  <StraightenIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    Length (cm)
                                  </Typography>
                                </Stack>
                                <NumberFieldFormInput
                                  name={`packages[${index}].depth`}
                                  label=""
                                  required
                                  min={1}
                                  control={control}
                                  error={errors?.packages?.[index]?.depth}
                                  wholeNumbersOnly
                                />
                              </Box>
                              <Box>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                  <StraightenIcon
                                    sx={{
                                      fontSize: 14,
                                      color: 'text.secondary',
                                      transform: 'rotate(90deg)'
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    Width (cm)
                                  </Typography>
                                </Stack>
                                <NumberFieldFormInput
                                  name={`packages[${index}].width`}
                                  label=""
                                  required
                                  min={1}
                                  control={control}
                                  error={errors?.packages?.[index]?.width}
                                  wholeNumbersOnly
                                />
                              </Box>
                              <Box>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                  <StraightenIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    Height (cm)
                                  </Typography>
                                </Stack>
                                <NumberFieldFormInput
                                  name={`packages[${index}].height`}
                                  label=""
                                  required
                                  min={1}
                                  control={control}
                                  error={errors?.packages?.[index]?.height}
                                  wholeNumbersOnly
                                />
                              </Box>
                              <Box>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                  <ScaleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    Weight (kg)
                                  </Typography>
                                </Stack>
                                <NumberFieldFormInput
                                  name={`packages[${index}].weight`}
                                  label=""
                                  required
                                  min={1}
                                  control={control}
                                  error={errors?.packages?.[index]?.weight}
                                />
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                ))}
              </Stack>

              <StyledButton
                variant="outlined"
                onClick={handleAddPackage}
                fullWidth
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  borderRadius: 2,
                  paddingY: 1,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  '&:hover': {
                    borderWidth: 2,
                    borderStyle: 'dashed',
                    borderColor: 'grey.400',
                    backgroundColor: 'grey.50'
                  }
                }}>
                Add Another Package
              </StyledButton>
            </Stack>
          </Box>

          {/* Footer Actions */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: 'white',
              borderTop: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.08)'
            }}>
            {isOnline ? (
              <StyledButton
                variant="contained"
                type="submit"
                fullWidth
                startIcon={<LocalShippingIcon />}
                sx={{
                  paddingY: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2
                }}>
                {buttonText}
              </StyledButton>
            ) : (
              <Tooltip title="You are offline. This feature is not available when offline." enterTouchDelay={0}>
                <span style={{ width: '100%' }}>
                  <StyledButton
                    variant="contained"
                    disabled
                    fullWidth
                    startIcon={<LocalShippingIcon />}
                    sx={{
                      paddingY: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 2
                    }}>
                    {buttonText}
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Stack>
      </form>
    </Dialog>
  );
}
